import { useState, useMemo } from 'react';
import { Play, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { JsonSchema } from '../types/workflow';

interface FieldDef {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  default?: unknown;
  nested?: FieldDef[];
}

function parseSchema(schema?: string | JsonSchema): JsonSchema | undefined {
  if (!schema) return undefined;
  if (typeof schema === 'string') {
    try {
      const parsed = JSON.parse(schema);
      return parsed.json ?? parsed;
    } catch {
      return undefined;
    }
  }
  return schema;
}

function extractFields(schema?: string | JsonSchema): FieldDef[] {
  const parsed = parseSchema(schema);
  if (!parsed?.properties) return [];
  const required = new Set(parsed.required ?? []);

  return Object.entries(parsed.properties).map(([name, prop]) => {
    const p = prop as Record<string, unknown>;
    const field: FieldDef = {
      name,
      type: (p.type as string) ?? 'string',
      description: p.description as string | undefined,
      required: required.has(name),
      default: p.default,
    };
    // Handle nested objects (e.g. dateRange with from/to)
    if (p.type === 'object' && p.properties) {
      const nestedRequired = new Set((p.required as string[]) ?? []);
      field.nested = Object.entries(p.properties as Record<string, Record<string, unknown>>).map(
        ([nName, nProp]) => ({
          name: nName,
          type: (nProp.type as string) ?? 'string',
          description: nProp.description as string | undefined,
          required: nestedRequired.has(nName),
          default: nProp.default,
        }),
      );
    }
    return field;
  });
}

function formatLabel(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface WorkflowInputFormProps {
  inputSchema?: string | JsonSchema;
  isPending: boolean;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
}

export function WorkflowInputForm({ inputSchema, isPending, onSubmit, onCancel }: WorkflowInputFormProps) {
  const fields = useMemo(() => extractFields(inputSchema), [inputSchema]);
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const defaults: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.default !== undefined) {
        defaults[f.name] = f.default;
      } else if (f.nested) {
        const nested: Record<string, unknown> = {};
        for (const nf of f.nested) {
          if (nf.default !== undefined) nested[nf.name] = nf.default;
        }
        if (Object.keys(nested).length > 0) defaults[f.name] = nested;
      }
    }
    return defaults;
  });

  const setValue = (name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const setNestedValue = (parent: string, name: string, value: unknown) => {
    setValues((prev) => ({
      ...prev,
      [parent]: { ...((prev[parent] as Record<string, unknown>) ?? {}), [name]: value },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert numeric fields
    const data: Record<string, unknown> = {};
    for (const f of fields) {
      const val = values[f.name];
      if (val === undefined || val === '') continue;
      if (f.type === 'number' && typeof val === 'string') {
        data[f.name] = Number(val);
      } else {
        data[f.name] = val;
      }
    }
    onSubmit(data);
  };

  if (fields.length === 0) {
    // No input needed — just confirm
    return (
      <div className="border rounded-lg bg-muted/30 p-4 space-y-3">
        <p className="text-sm text-muted-foreground">This workflow has no required inputs.</p>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSubmit({})} disabled={isPending} className="gap-1.5">
            {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            {isPending ? 'Running...' : 'Run Now'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border rounded-lg bg-muted/30 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Workflow Input</h3>
        <button type="button" onClick={onCancel} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {fields.map((field) => {
          if (field.nested) {
            return (
              <div key={field.name} className="col-span-2 space-y-2">
                <Label className="text-xs font-medium">{formatLabel(field.name)}</Label>
                <div className="grid grid-cols-2 gap-2 pl-3 border-l-2 border-slate-700">
                  {field.nested.map((nf) => (
                    <div key={nf.name} className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        {formatLabel(nf.name)}
                        {nf.required && <span className="text-red-400 ml-0.5">*</span>}
                      </Label>
                      <Input
                        type={nf.type === 'number' ? 'number' : 'text'}
                        placeholder={nf.description || nf.name}
                        value={String(((values[field.name] as Record<string, unknown>) ?? {})[nf.name] ?? '')}
                        onChange={(e) => setNestedValue(field.name, nf.name, e.target.value)}
                        required={nf.required}
                        className="h-8 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={field.name} className="space-y-1">
              <Label className="text-xs font-medium">
                {formatLabel(field.name)}
                {field.required && <span className="text-red-400 ml-0.5">*</span>}
              </Label>
              <Input
                type={field.type === 'number' ? 'number' : 'text'}
                placeholder={field.description || field.name}
                value={String(values[field.name] ?? '')}
                onChange={(e) => setValue(field.name, e.target.value)}
                required={field.required}
                className="h-8 text-sm"
              />
              {field.description && (
                <p className="text-[10px] text-muted-foreground">{field.description}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={isPending} className="gap-1.5">
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          {isPending ? 'Running...' : 'Run Workflow'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
